import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trades } from "./data";
import { SectionHeading } from "./Engines";

const statusTone = {
  Executed: "text-bull border-bull/40 bg-bull/10",
  Pending: "text-warn border-warn/40 bg-warn/10",
  Closed: "text-muted-foreground border-border bg-surface-2/60",
};

export function AutoTrade() {
  const [auto, setAuto] = useState(true);

  return (
    <section id="execution" className="relative py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <SectionHeading
            eyebrow="Auto Execution"
            title="Trades routed the moment conviction clears risk."
            sub="Entry, invalidation and staged targets are published before the fill — never after."
          />
          <div className="glass mb-12 flex items-center gap-4 rounded-2xl px-5 py-4">
            <div>
              <p className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Auto Trade</p>
              <p className={`font-mono text-lg ${auto ? "text-bull" : "text-muted-foreground"}`}>
                {auto ? "ON" : "OFF"}
              </p>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={auto}
                onChange={() => setAuto((v) => !v)}
                aria-label="Auto trade"
                className="peer sr-only"
              />
              <span className="peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 h-5 w-9 rounded-full border border-transparent bg-input shadow-sm transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-background after:shadow-lg after:transition-transform peer-checked:after:translate-x-4 relative block after:block" />
            </label>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {trades.map((t, i) => (
            <motion.div
              key={t.pair}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="glass glass-hover rounded-2xl p-6"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="truncate font-mono text-lg font-semibold">{t.pair}</p>
                  <p
                    className={`text-xs tracking-[0.2em] uppercase ${
                      t.side === "LONG" ? "text-bull" : "text-bear"
                    }`}
                  >
                    {t.side}
                  </p>
                </div>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={`${t.status}-${auto}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`shrink-0 rounded-full border px-3 py-1 text-[10px] tracking-[0.16em] uppercase ${
                      statusTone[auto ? t.status : "Pending"]
                    }`}
                  >
                    {auto ? t.status : "Paused"}
                  </motion.span>
                </AnimatePresence>
              </div>

              <dl className="mt-5 grid grid-cols-2 gap-3 font-mono text-sm">
                {[
                  ["Entry", t.entry],
                  ["Stop Loss", t.sl],
                  ["Target 1", t.t1],
                  ["Target 2", t.t2],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-border/60 bg-surface/50 p-3">
                    <dt className="text-[10px] tracking-[0.16em] text-muted-foreground uppercase">
                      {k}
                    </dt>
                    <dd className="mt-1">{v}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-5">
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Trade lifecycle</span>
                  <span className="font-mono text-primary">AI Confidence {t.confidence}%</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${auto ? t.progress : 0}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.1, ease: "easeOut" }}
                    className="h-full rounded-full"
                    style={{ backgroundImage: "var(--gradient-gold)" }}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
